import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowLeft, ShieldAlert, AlertTriangle, Scale, Lock, Ban, ShoppingBag, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  description?: string;
  items: FAQItem[];
}

export function FAQsPage() {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleCategory = (categoryId: string) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
    setOpenQuestion(null); // Close any open question when toggling category
  };

  const toggleQuestion = (questionId: string) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  const faqCategories: FAQCategory[] = [
    {
      id: 'refunds',
      title: 'Politique de Remboursement',
      icon: <ShoppingBag className="h-6 w-6 text-blue-500" />,
      items: [
        {
          question: 'Puis-je être remboursé ?',
          answer: 'Oui, un remboursement peut être effectué en cas de produit non livré, endommagé ou non conforme à la description. La demande doit être faite dans les 48h suivant la réception.'
        },
        {
          question: 'Comment demander un remboursement ?',
          answer: 'Contacte directement le vendeur via la page de commande. Si aucun accord n\'est trouvé, contacte notre support avec les preuves nécessaires.'
        },
        {
          question: 'Quels sont les délais de remboursement ?',
          answer: 'Une fois la demande approuvée, le remboursement est traité dans un délai de 5 à 10 jours ouvrables, selon la méthode de paiement utilisée.'
        },
        {
          question: 'Puis-je être remboursé partiellement ?',
          answer: 'Oui, dans certains cas, un remboursement partiel peut être accordé si seulement une partie de la commande pose problème ou si un arrangement est trouvé avec le vendeur.'
        }
      ]
    },
    {
      id: 'delivery-warnings',
      title: 'Avertissements pour les Livreurs',
      icon: <ShieldAlert className="h-6 w-6 text-red-500" />,
      items: [
        {
          question: 'Zones interdites à la livraison',
          answer: (
            <div>
              <p>Il est formellement interdit de livrer dans des zones où se déroulent :</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Manifestations</li>
                <li>Guerres civiles</li>
                <li>Périodes de troubles graves</li>
                <li>Zones à haut risque (à la discrétion du livreur ou des autorités locales)</li>
              </ul>
            </div>
          )
        },
        {
          question: 'Sécurité personnelle',
          answer: 'La sécurité du livreur passe avant toute livraison. En cas de doute, il ne faut pas livrer.'
        },
        {
          question: 'Confidentialité',
          answer: 'Le livreur s\'engage à ne jamais divulguer les informations personnelles (adresse, numéro, commande, etc.) des clients, sous peine de suspension définitive.'
        },
        {
          question: 'Que faire en cas d\'agression ?',
          answer: 'En cas d\'agression ou de menace, quittez immédiatement les lieux, mettez-vous en sécurité et contactez les autorités. Informez ensuite notre équipe de support dès que possible.'
        }
      ]
    },
    {
      id: 'seller-warnings',
      title: 'Avertissements pour les Vendeurs',
      icon: <AlertTriangle className="h-6 w-6 text-yellow-500" />,
      items: [
        {
          question: 'Produits interdits',
          answer: (
            <div>
              <p>Il est strictement interdit de vendre :</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Produits illégaux</li>
                <li>Médicaments non autorisés</li>
                <li>Armes ou objets dangereux</li>
                <li>Produits contrefaits</li>
              </ul>
            </div>
          )
        },
        {
          question: 'Confidentialité',
          answer: 'Les vendeurs doivent respecter la confidentialité des clients. Toute fuite ou revente de données personnelles entraînera une suspension immédiate.'
        },
        {
          question: 'Quelles sont les sanctions en cas de fraude ?',
          answer: 'En cas de fraude avérée, le compte vendeur sera immédiatement suspendu, les fonds bloqués et des poursuites judiciaires pourront être engagées selon la gravité des faits.'
        },
        {
          question: 'Comment éviter les litiges avec les clients ?',
          answer: 'Pour éviter les litiges, assurez-vous de fournir des descriptions précises et des photos réelles de vos produits, d\'emballer soigneusement les articles et de communiquer clairement sur les délais de livraison.'
        }
      ]
    },
    {
      id: 'data-justice',
      title: 'Données et Justice',
      icon: <Scale className="h-6 w-6 text-purple-500" />,
      items: [
        {
          question: 'Mandat officiel',
          answer: 'Les données des utilisateurs (clients, livreurs ou vendeurs) peuvent être remises aux autorités uniquement sur présentation d\'un mandat légal. Nous respectons les lois en vigueur et collaborons avec les forces de l\'ordre en cas d\'enquête.'
        },
        {
          question: 'Comment sont traitées les demandes des autorités ?',
          answer: 'Toute demande officielle des autorités est traitée avec la plus grande attention par notre service juridique. Nous vérifions la légalité de chaque demande avant de fournir les informations requises.'
        },
        {
          question: 'Les utilisateurs sont-ils informés des demandes légales ?',
          answer: 'Dans la mesure où la loi le permet, nous informons les utilisateurs concernés par une demande légale d\'accès à leurs données, sauf si une ordonnance de non-divulgation nous l\'interdit.'
        }
      ]
    },
    {
      id: 'confidentiality',
      title: 'Confidentialité Générale',
      icon: <Lock className="h-6 w-6 text-green-500" />,
      items: [
        {
          question: 'Protection des données',
          answer: 'Toutes les données sur AEM Marketplace sont protégées. Nous ne partageons aucune information sans autorisation, sauf en cas légal.'
        },
        {
          question: 'Utilisation responsable',
          answer: 'Toute tentative de vol, fraude ou utilisation abusive de notre plateforme entraînera une exclusion définitive et pourra faire l\'objet de poursuites judiciaires.'
        },
        {
          question: 'Comment sont stockées mes données personnelles ?',
          answer: 'Vos données personnelles sont stockées sur des serveurs sécurisés avec un chiffrement de bout en bout. Nous utilisons des protocoles de sécurité avancés pour protéger vos informations contre tout accès non autorisé.'
        },
        {
          question: 'Puis-je demander la suppression de mes données ?',
          answer: 'Oui, conformément aux lois sur la protection des données, vous pouvez demander l\'accès, la rectification ou la suppression de vos données personnelles en contactant notre service client.'
        }
      ]
    },
    {
      id: 'liability-limitations',
      title: 'Limitations de Responsabilité',
      icon: <Ban className="h-6 w-6 text-red-500" />,
      items: [
        {
          question: 'Incidents graves (kidnapping, violence, etc.)',
          answer: 'AEM Marketplace n\'est pas responsable en cas d\'incidents graves liés à la livraison (kidnapping, violences, braquages, etc.). C\'est pourquoi nous interdisons formellement les livraisons dans des zones instables ou dangereuses.'
        },
        {
          question: 'Quelle est la responsabilité d\'AEM en cas de produit défectueux ?',
          answer: 'AEM Marketplace agit comme intermédiaire entre acheteurs et vendeurs. La responsabilité concernant la qualité des produits incombe principalement au vendeur. Toutefois, nous facilitons la résolution des litiges et pouvons intervenir pour des remboursements si nécessaire.'
        },
        {
          question: 'AEM est-il responsable des retards de livraison ?',
          answer: 'AEM Marketplace n\'est pas directement responsable des retards de livraison, surtout ceux causés par des événements imprévisibles (conditions météorologiques, problèmes de circulation, etc.). Nous nous efforçons néanmoins de vous tenir informé et de trouver des solutions alternatives.'
        },
        {
          question: 'Que se passe-t-il en cas de catastrophe naturelle ?',
          answer: 'En cas de catastrophe naturelle ou d\'événement de force majeure, AEM Marketplace peut suspendre temporairement ses services dans les zones affectées. Les commandes en cours seront traitées au cas par cas, avec priorité à la sécurité de tous.'
        }
      ]
    },
    {
      id: 'payment',
      title: 'Paiements et Transactions',
      icon: <ShoppingBag className="h-6 w-6 text-blue-500" />,
      items: [
        {
          question: 'Quels moyens de paiement sont acceptés ?',
          answer: 'AEM Marketplace accepte plusieurs moyens de paiement, notamment les cartes bancaires, PayPal, et dans certains cas, le paiement à la livraison. Les options disponibles dépendent du vendeur et de votre région.'
        },
        {
          question: 'Les transactions sont-elles sécurisées ?',
          answer: 'Oui, toutes les transactions en ligne sur AEM Marketplace sont sécurisées par un chiffrement SSL. Nous ne stockons jamais vos informations de carte bancaire complètes sur nos serveurs.'
        },
        {
          question: 'Comment fonctionne le paiement à la livraison ?',
          answer: 'Pour le paiement à la livraison, vous réglez directement le livreur au moment de recevoir votre commande. Un code de confirmation vous sera fourni pour sécuriser la transaction et confirmer la livraison.'
        },
        {
          question: 'Que faire en cas de problème avec un paiement ?',
          answer: 'Si vous rencontrez un problème avec un paiement, contactez immédiatement notre service client avec les détails de la transaction. Nous vous aiderons à résoudre le problème dans les plus brefs délais.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Gestion de Compte',
      icon: <User className="h-6 w-6 text-indigo-500" />,
      items: [
        {
          question: 'Comment modifier mes informations personnelles ?',
          answer: 'Vous pouvez modifier vos informations personnelles en accédant à votre profil depuis le menu utilisateur. Cliquez sur "Paramètres" pour mettre à jour vos coordonnées, mot de passe et préférences.'
        },
        {
          question: 'Comment supprimer mon compte ?',
          answer: 'Pour supprimer votre compte, contactez notre service client avec votre demande. Notez que la suppression de votre compte entraînera la perte définitive de votre historique de commandes et autres données associées.'
        },
        {
          question: 'Que faire si mon compte est piraté ?',
          answer: 'Si vous suspectez que votre compte a été compromis, changez immédiatement votre mot de passe et contactez notre service client. Nous prendrons les mesures nécessaires pour sécuriser votre compte et enquêter sur l\'incident.'
        },
        {
          question: 'Puis-je avoir plusieurs comptes ?',
          answer: 'Non, chaque utilisateur ne peut avoir qu\'un seul compte personnel. La création de comptes multiples peut entraîner la suspension de tous vos comptes. Les entreprises peuvent toutefois demander des comptes professionnels distincts.'
        }
      ]
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <Link to="/">
            <Button variant="ghost" className="flex items-center text-gray-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>

        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
            Foire Aux Questions
          </h1>
          <p className="mt-3 sm:mt-4 text-base sm:text-xl text-gray-500 max-w-3xl mx-auto">
            Trouvez des réponses aux questions les plus fréquentes concernant notre plateforme, les livraisons, les paiements et plus encore.
          </p>
        </div>

        <div className="space-y-4 sm:space-y-8">
          {faqCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center">
                  <div className={`${openCategory === category.id ? 'text-primary' : ''}`}>
                    {category.icon}
                  </div>
                  <h2 className="ml-3 text-lg sm:text-xl font-semibold">
                    {category.title}
                  </h2>
                </div>
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${
                    openCategory === category.id ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              {openCategory === category.id && (
                <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                  {category.description && (
                    <p className="text-gray-600 mb-4">{category.description}</p>
                  )}
                  <div className="space-y-3 sm:space-y-4">
                    {category.items.map((item, index) => (
                      <div key={index} className="border-b border-gray-100 last:border-b-0 pb-3 sm:pb-4 last:pb-0">
                        <button
                          className="w-full flex items-center justify-between text-left"
                          onClick={() => toggleQuestion(`${category.id}-${index}`)}
                        >
                          <h3 className="text-base sm:text-lg font-medium text-gray-900">{item.question}</h3>
                          <ChevronDown
                            className={`h-5 w-5 text-gray-500 transition-transform ${
                              openQuestion === `${category.id}-${index}` ? 'transform rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openQuestion === `${category.id}-${index}` && (
                          <div className="mt-2 text-sm sm:text-base text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-lg">
                            {typeof item.answer === 'string' ? (
                              <p>{item.answer}</p>
                            ) : (
                              item.answer
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 sm:mt-12 bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Vous n'avez pas trouvé votre réponse ?</h2>
          <p className="text-gray-600 mb-4 sm:mb-6">
            Notre équipe de support est disponible pour répondre à toutes vos questions. N'hésitez pas à nous contacter.
          </p>
          <Link to="/contact">
            <Button variant="primary" className="w-full sm:w-auto">
              Contactez-nous
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}